import {
    CHARACTER_POSITIONS,
    CHARACTER_SIZES,
    Z_INDEX
} from './constants.js';
import { CDN_URL } from '../../../js/config.js';
import { initializeImageZoom } from './imageZoom.js';
import { applySavedState } from './imageZoom.js';
import { HangoutState } from '../state/HangoutState.js';
import { HangoutService } from '../services/HangoutService.js';
import { attachSpriteContextMenuListeners, isImageElement, isVideoElement } from './SpriteContextMenu.js';
import { resolveMessageSpriteData } from '../shared/sprite-state.js';
import { hasPlayableCharacterVideo } from '../../utils/spriteVideoStatus.js';
import { storage } from '../../utils/storage.js';
import { fetchCache } from '../../utils/fetchCache.js';
import { getDefaultStandingActionId, resolveLegacySpriteAction } from '../../utils/spriteFallbacks.js';
import { normalizeGender } from '../../utils/genderUtils.js';

// Constants
const DEFAULT_SPRITE = '/assets/images/kiss.webp';
const ANIMATION_DURATION = 800;
const FADE_DURATION = 300;
const CHARACTER_STAGGER_DELAY = 150;
const CHARACTER_SPRITE_ASPECT_RATIO = '7 / 9';
const CHARACTER_INTRINSIC_WIDTH = 1120;
const CHARACTER_INTRINSIC_HEIGHT = 1440;

export class CharacterManager {
    constructor() {
        this.characterContainers = new Map();
        this.characterSpriteCache = new Map();
        this._storageManager = null;
    }

    _hideOriginalContainerIfMultiReady(hangoutConfig) {
        const original = document.querySelector('.character-image:not([class*="character-image-"])');
        if (!original) return;

        // Only hide the original single-character container once ALL multi-char containers exist
        // This prevents the "disappearing character" issue where the original hides before
        // the new layout is fully visible
        const expectedCount = hangoutConfig?.allCharacters?.length || 1;
        if (expectedCount <= 1) return;

        const existingContainers = document.querySelectorAll('[class*="character-image-"]');
        if (existingContainers.length < expectedCount) {
            // Not all containers created yet - keep original visible
            return;
        }

        const multiMedia0 = document.querySelector('.character-image-0 img, .character-image-0 video');
        const multiSrc = multiMedia0?.getAttribute?.('src') || (multiMedia0?.src || '');
        if (!multiSrc) return;

        (original).style.display = 'none';
    }

    async _getStorageManager() {
        if (!this._storageManager) {
            const { storageManager } = await import('../../../core/storage/index.js');
            this._storageManager = storageManager;
        }
        return this._storageManager;
    }

    async updateCharacterImage(spriteUrl, characterName, index = 0, spriteState = null, isInitialSetup = false, isVideo = false, options = {}) {
        const storageManager = await this._getStorageManager();
        const hangoutConfig = await storageManager.getItem('currentHangoutConfig');
        const isMultiCharacter = hangoutConfig?.allCharacters?.length > 1;

        if (isMultiCharacter) {
            await this.updateMultiCharacterImage(spriteUrl, characterName, index, spriteState, hangoutConfig, isInitialSetup, isVideo, options);
        } else {
            await this.updateSingleCharacterImage(spriteUrl, characterName, spriteState, hangoutConfig, isInitialSetup, isVideo);
        }

        // Always ensure the sprite is visible (cinematic intro disabled while animation system is stubbed)
        if (isInitialSetup) {
            // prepare off-screen state for a simple slide-in effect
            this.hideCharacterForCinematicIntro(index, isMultiCharacter);
        } else {
            this.ensureCharacterVisibility(index, isMultiCharacter);
        }
    }

    async updateMultiCharacterImage(spriteUrl, characterName, index, spriteState, hangoutConfig, isInitialSetup = false, isVideo = false, options = {}) {
        if (!options.skipLeftoverCleanup) {
            await this.cleanupLeftoverSprites();
        }
        this._cleanupInactiveContainers(hangoutConfig);

        // Determine intended character count
        const currentCharacterCount = hangoutConfig?.allCharacters?.length || 1;

        let characterContainer = document.querySelector(`.character-image-${index}`);
        const isNewContainer = !characterContainer;

        if (isNewContainer) {
            // createCharacterContainer now handles positioning internally BEFORE DOM append
            characterContainer = this.createCharacterContainer(index, characterName, hangoutConfig);

            // Seamless single → multi transition:
            // If we hide the original single container before the new multi container is painted,
            // the character appears to "disappear" while the new sprite loads.
            if (index === 0) {
                const originalImg = /** @type {HTMLImageElement|null} */ (document.querySelector('.character-image:not([class*="character-image-"]) img'));
                const originalSrc = originalImg?.getAttribute?.('src') || '';

                if (originalSrc) {
                    const placeholder = document.createElement('img');
                    placeholder.src = originalSrc;
                    placeholder.alt = originalImg?.alt || `${characterName} sprite`;
                    placeholder.width = CHARACTER_INTRINSIC_WIDTH;
                    placeholder.height = CHARACTER_INTRINSIC_HEIGHT;
                    placeholder.style.cssText = `
                        width: auto;
                        height: ${CHARACTER_SIZES.normal.height};
                        aspect-ratio: ${CHARACTER_SPRITE_ASPECT_RATIO};
                        object-fit: contain;
                        object-position: bottom center;
                        max-width: 100%;
                        max-height: 100%;
                        opacity: 1;
                        transition: opacity 0.3s ease;
                        filter: drop-shadow(2px 2px 4px rgba(0,0,0,0.3));
                    `;
                    characterContainer.appendChild(placeholder);
                }
            }
        } else if (!isInitialSetup) {
            this._showContainer(characterContainer);
        }

        const resolvedCharacterId = this._resolveCharacterIdForUpdate(hangoutConfig, characterName, index, spriteState);
        if (resolvedCharacterId != null) {
            characterContainer.setAttribute('data-character-id', resolvedCharacterId);
        }

        await this.updateCharacterImageElement(characterContainer, spriteUrl, characterName, spriteState, hangoutConfig, index, isVideo);

        // Count containers after this update
        const containersNow = document.querySelectorAll('[class*="character-image-"]').length;
        const allContainersExist = containersNow >= currentCharacterCount;

        // Only hide original when ALL containers exist
        // Skip repositioning since containers are already positioned during creation
        if (allContainersExist) {
            this._hideOriginalContainerIfMultiReady(hangoutConfig);
        } else {
        }
    }

    async updateSingleCharacterImage(spriteUrl, characterName, spriteState, hangoutConfig, isInitialSetup = false, isVideo = false) {
        this._hideMultiCharacterContainers();

        const characterImageContainer = /** @type {HTMLElement|null} */ (document.querySelector('.character-image:not([class*="character-image-"])'));
        if (!characterImageContainer) return;

        if (characterImageContainer.classList.contains('user-positioned')) {
            // A drag owns the position. Layout and sprite swaps must not move it.
        } else if (window.innerWidth >= 1025) {
            this._clearInlineCharacterPosition(characterImageContainer);
        } else {
            characterImageContainer.style.setProperty('--character-transform', 'translateX(-50%)');
            characterImageContainer.style.setProperty('transform', 'translateX(-50%)', 'important');
            characterImageContainer.style.setProperty('left', '50%', 'important');
            characterImageContainer.style.setProperty('right', 'auto', 'important');
        }
        (/** @type {any} */ (characterImageContainer))._positioningEnabled = true;

        const resolvedCharacterId = this._resolveCharacterIdForUpdate(hangoutConfig, characterName, 0, spriteState);
        if (resolvedCharacterId != null) {
            characterImageContainer.setAttribute('data-character-id', resolvedCharacterId);
        }

        await this.updateCharacterImageElement(characterImageContainer, spriteUrl, characterName, spriteState, hangoutConfig, 0, isVideo);

        if (!isInitialSetup) {
            this._showContainer(characterImageContainer);
        }
    }

    _cleanupInactiveContainers(hangoutConfig) {
        const activeCharacterIds = hangoutConfig.allCharacters.map(char => String(char.id));
        const allContainers = document.querySelectorAll('[class*="character-image-"]');

        allContainers.forEach(container => {
            const containerIndex = parseInt(container.className.match(/character-image-(\d+)/)?.[1]);
            const characterId = container.getAttribute('data-character-id');

            const shouldRemove = (characterId && !activeCharacterIds.includes(characterId)) ||
                (!isNaN(containerIndex) && containerIndex >= hangoutConfig.allCharacters.length);

            if (shouldRemove) {
                container.remove();
            } else if (!isNaN(containerIndex)) {
                this._removeDuplicateContainers(containerIndex);
            }
        });
    }

    _removeDuplicateContainers(index) {
        const containers = document.querySelectorAll(`.character-image-${index}`);
        for (let i = 1; i < containers.length; i++) {
            containers[i].remove();
        }
    }

    _hideMultiCharacterContainers() {
        const containers = document.querySelectorAll('[class*="character-image-"]');
        containers.forEach(container => {
            (container).style.display = 'none';
            (container).style.opacity = '0';
        });
    }

    _isUnindexedCharacterContainer(container) {
        return !/\bcharacter-image-\d+\b/.test(container?.className || '');
    }

    _isDesktopSingleContainer(container) {
        return window.innerWidth >= 1025 && this._isUnindexedCharacterContainer(container);
    }

    _clearInlineCharacterPosition(container) {
        container.style.removeProperty('left');
        container.style.removeProperty('right');
        container.style.removeProperty('transform');
        container.style.setProperty('--character-transform', 'translateX(0)');
    }

    _hideOriginalContainer() {
        const container = document.querySelector('.character-image:not([class*="character-image-"])');
        if (container) (container).style.display = 'none';
    }

    _showContainer(container) {
        (container).style.display = 'flex';
        (container).style.opacity = '1';
    }

    createCharacterContainer(index, characterName, hangoutConfig) {
        const container = document.createElement('div');
        container.className = `character-image character-image-${index}`;

        // Calculate position BEFORE appending to DOM to prevent repositioning flash
        const totalCharacters = hangoutConfig?.allCharacters?.length || 1;

        // CRITICAL: On mobile portrait, ALL characters must be centered from the start
        // Only one character is visible at a time via CSS .currently-speaking
        const isMobilePortrait = window.innerWidth <= 1024 && window.innerHeight > window.innerWidth;

        let positionStyles = '';
        let transform = 'translateX(0)';

        if (isMobilePortrait) {
            // Center ALL characters on mobile portrait - CSS handles visibility
            // Use !important to override any CSS rules
            positionStyles = 'left: 50% !important; right: auto !important; transform: translateX(-50%) !important;';
            transform = 'translateX(-50%)';
        } else {
            // Desktop: use standard positions
            const positions = this._getPositionsForCount(totalCharacters);
            const position = positions[index] || positions[0];
            transform = position?.transform || 'translateX(0)';

            if (position) {
                if (position.left) positionStyles += `left: ${position.left};`;
                if (position.right) positionStyles += `right: ${position.right};`;
                if (position.transform) positionStyles += `transform: ${position.transform};`;
            }
        }

        // Set CSS variable FIRST - this is critical for animation end state
        container.style.setProperty('--character-transform', transform);

        (container).style.cssText = `
            position: absolute;
            display: flex;
            align-items: flex-end;
            justify-content: center;
            pointer-events: auto;
            z-index: ${Z_INDEX.characters};
            width: 700px;
            height: 100%;
            cursor: pointer;
            ${positionStyles}
        `;

        container.setAttribute('data-character-index', index);
        container.setAttribute('data-character-name', characterName);
        container._positioningEnabled = true;

        const mainContainer = document.getElementById('hangout-container');
        if (mainContainer) {
            mainContainer.appendChild(container);
        }

        return container;
    }

    _getPositionsForCount(totalCharacters) {
        if (totalCharacters === 1) return CHARACTER_POSITIONS.single;
        if (totalCharacters === 2) return CHARACTER_POSITIONS.dual;
        return CHARACTER_POSITIONS.triple;
    }

    positionCharacterContainer(container, index, totalCharacters) {
        // Respect user-placed containers; do not override with defaults
        if (container && container.classList && container.classList.contains('user-positioned')) {
            return;
        }

        // On mobile portrait, center all characters (only one shows at a time via .currently-speaking)
        const isMobilePortrait = window.innerWidth <= 1024 && window.innerHeight > window.innerWidth;
        if (isMobilePortrait && totalCharacters > 1) {
            // Clear any existing positioning
            (container).style.left = '';
            (container).style.right = '';
            (container).style.transform = '';

            // Center all characters - CSS will show/hide via .currently-speaking
            const centeringStyle = { left: '50%', right: 'auto', transform: 'translateX(-50%)' };
            Object.assign((container).style, centeringStyle);

            // Set CSS variable for animation/hover preservation
            container.style.setProperty('--character-transform', 'translateX(-50%)');

            container._positioningEnabled = true;
            return;
        }

        let positions;
        if (totalCharacters === 1) positions = CHARACTER_POSITIONS.single;
        else if (totalCharacters === 2) positions = CHARACTER_POSITIONS.dual;
        else positions = CHARACTER_POSITIONS.triple;

        if (positions[index]) {
            const expectedPos = positions[index];
            const currentLeft = (container).style.left;
            const currentRight = (container).style.right;

            // Only clear and reapply if position actually differs
            const needsUpdate =
                (expectedPos.left && currentLeft !== expectedPos.left) ||
                (expectedPos.right && currentRight !== expectedPos.right) ||
                !container._positioningEnabled;

            if (needsUpdate) {
                // Clear any existing positioning first
                (container).style.left = '';
                (container).style.right = '';
                (container).style.transform = '';

                // Apply new positioning
                Object.assign((container).style, positions[index]);
            }

            // Always ensure CSS variable is set for animation/hover preservation
            const transform = positions[index].transform || 'translateX(0)';
            container.style.setProperty('--character-transform', transform);

            // Mark container as positioned (for debugging)
            container._positioningEnabled = true;
        }
    }

    async updateCharacterImageElement(characterContainer, spriteUrl, characterName, spriteState, hangoutConfig, index, isVideo = false) {
        const existingImg = characterContainer.querySelector('img');
        const existingVideo = characterContainer.querySelector('video');
        const hasRealExistingImage = existingImg && existingImg.src && existingImg.src !== '' && !existingImg.src.endsWith('/');
        const hasRealExistingVideo = existingVideo && existingVideo.src && existingVideo.src !== '' && !existingVideo.src.endsWith('/');
        const isFirstLoad = !(hasRealExistingImage || hasRealExistingVideo);

        const nextGen = String((Number(characterContainer.dataset.spriteLoadGen) || 0) + 1);
        characterContainer.dataset.spriteLoadGen = nextGen;

        characterContainer.querySelectorAll('img, video').forEach((el) => el.remove());

        const baseHeight = CHARACTER_SIZES.normal.height;
        const isMobilePortrait = window.innerWidth <= 1024 && window.innerHeight > window.innerWidth;
        const isMultiCharacterContainer = /\bcharacter-image-\d+\b/.test(characterContainer.className || '');
        const objectFit = (isMobilePortrait || isMultiCharacterContainer) ? 'contain' : 'cover';
        const objectPosition = objectFit === 'contain' ? 'bottom center' : 'center';
        const maxConstraint = isMobilePortrait ? 'none' : '100%';
        const mediaStyle = `
            width: auto;
            height: ${baseHeight};
            aspect-ratio: ${CHARACTER_SPRITE_ASPECT_RATIO};
            object-fit: ${objectFit};
            object-position: ${objectPosition};
            max-width: ${maxConstraint};
            max-height: ${maxConstraint};
            opacity: 1;
            transition: opacity 0.3s ease;
            filter: drop-shadow(2px 2px 4px rgba(0,0,0,0.3));
        `;

        if (isVideo) {
            const characterVideo = document.createElement('video');
            characterVideo.setAttribute('aria-label', `${characterName} image`);
            characterVideo.loop = true;
            characterVideo.muted = true;
            characterVideo.playsInline = true;
            (/** @type {HTMLElement} */ (characterVideo)).style.cssText = mediaStyle;
            characterContainer.appendChild(characterVideo);
            if (spriteUrl?.trim()) {
                const fallbackSpriteUrl = this._deriveStillSpriteUrlFromVideoUrl(spriteUrl);
                await this._loadCharacterVideo(
                    characterVideo,
                    spriteUrl,
                    characterName,
                    spriteState,
                    characterContainer,
                    hangoutConfig,
                    index,
                    fallbackSpriteUrl,
                    nextGen,
                    isFirstLoad
                );
            } else {
                (characterVideo).style.opacity = '0';
                initializeImageZoom();
            }
            return;
        }

        const characterImage = document.createElement('img');
        characterImage.alt = `${characterName} sprite`;
        characterImage.width = CHARACTER_INTRINSIC_WIDTH;
        characterImage.height = CHARACTER_INTRINSIC_HEIGHT;
        (characterImage).style.cssText = mediaStyle;
        characterContainer.appendChild(characterImage);
        if (spriteUrl?.trim()) {
            await this._loadCharacterImage(
                characterImage,
                spriteUrl,
                characterName,
                spriteState,
                characterContainer,
                hangoutConfig,
                index,
                nextGen,
                isFirstLoad
            );
        } else {
            (characterImage).style.opacity = '0';
            initializeImageZoom();
        }
    }

    async _loadCharacterImage(
        characterImage,
        spriteUrl,
        characterName,
        spriteState,
        characterContainer,
        hangoutConfig,
        index,
        loadGen = null,
        isFirstLoad = true
    ) {
        const isStale = () =>
            loadGen != null && characterContainer.dataset.spriteLoadGen !== String(loadGen);

        return new Promise((resolve) => {
            const newImage = new Image();

            const timeout = setTimeout(() => {
                if (isStale() || !characterImage.isConnected) {
                    resolve();
                    return;
                }
                console.warn(`⏰ Sprite load timeout for ${characterName}, falling back to default`);
                characterImage.src = DEFAULT_SPRITE;
                (characterImage).style.opacity = '1';
                initializeImageZoom();
                resolve();
            }, 5000);

            newImage.onload = () => {
                clearTimeout(timeout);
                if (isStale() || !characterImage.isConnected) {
                    resolve();
                    return;
                }

                characterImage.src = newImage.src;
                characterImage.alt = characterName;
                (characterImage).style.opacity = '1';

                if (isFirstLoad) {
                    const isMobilePortrait = window.innerWidth <= 1024 && window.innerHeight > window.innerWidth;

                    const computedTransform = getComputedStyle(characterContainer).getPropertyValue('--character-transform');
                    if (!computedTransform || computedTransform === '') {
                        if (isMobilePortrait) {
                            characterContainer.style.setProperty('--character-transform', 'translateX(-50%)');
                        } else {
                            const totalChars = hangoutConfig?.allCharacters?.length || 1;
                            const positions = this._getPositionsForCount(totalChars);
                            const position = positions[index] || positions[0];
                            characterContainer.style.setProperty('--character-transform', position?.transform || 'translateX(-50%)');
                        }
                    }

                    if (isMobilePortrait) {
                        characterContainer.style.setProperty('transform', 'translateX(-50%)', 'important');
                        characterContainer.style.setProperty('left', '50%', 'important');
                        characterContainer.style.setProperty('right', 'auto', 'important');
                        characterContainer.style.removeProperty('opacity');
                        characterContainer.style.removeProperty('visibility');
                    } else {
                        void characterContainer.offsetHeight;
                        characterContainer.classList.add('character-slide-in', 'slide-in-staggered');
                        const handleAnimEnd = () => {
                            characterContainer.classList.remove('character-slide-in', 'slide-in-staggered');
                            if (this._isDesktopSingleContainer(characterContainer) && !characterContainer.classList.contains('user-positioned')) {
                                this._clearInlineCharacterPosition(characterContainer);
                            } else {
                                const finalTransform = characterContainer.style.getPropertyValue('--character-transform');
                                if (finalTransform) {
                                    characterContainer.style.transform = finalTransform;
                                }
                            }
                            characterContainer.removeEventListener('animationend', handleAnimEnd);
                        };
                        characterContainer.addEventListener('animationend', handleAnimEnd);
                    }
                }

                try {
                    applySavedState(characterImage);
                } catch (e) {
                    console.warn('applySavedState failed:', e);
                }

                requestAnimationFrame(() => {
                    if (isStale() || !characterImage.isConnected) {
                        resolve();
                        return;
                    }
                    this.addCharacterInteractionEvents(characterImage, characterName, hangoutConfig, index);

                    try {
                        initializeImageZoom();
                    } catch (error) {
                        console.warn('Image zoom initialization failed:', error);
                    }
                    resolve();
                });
            };

            newImage.onerror = () => {
                clearTimeout(timeout);
                if (isStale() || !characterImage.isConnected) {
                    resolve();
                    return;
                }
                characterImage.src = DEFAULT_SPRITE;
                (characterImage).style.opacity = '1';
                initializeImageZoom();
                resolve();
            };

            newImage.src = spriteUrl;
        });
    }

    _deriveStillSpriteUrlFromVideoUrl(videoUrl) {
        // Video URL convention: .../s/videos/{char}_{outfit}_{action}_NNNNN.webm
        // Still sprite convention: .../s/sprites/{char}/{outfit}/{action}.webp
        const match = String(videoUrl || '').match(/\/s\/videos\/(\d+)_(\d+)_(\d+)_\d+\.webm/);
        if (!match) return DEFAULT_SPRITE;
        const [, characterId, outfitId, actionId] = match;
        return videoUrl.replace(/\/s\/videos\/.*$/, `/s/sprites/${characterId}/${outfitId}/${actionId}.webp`);
    }

    async _loadCharacterVideo(
        characterVideo,
        videoUrl,
        characterName,
        spriteState,
        characterContainer,
        hangoutConfig,
        index,
        fallbackSpriteUrl = DEFAULT_SPRITE,
        loadGen = null,
        isFirstLoad = true
    ) {
        const isStale = () =>
            loadGen != null && characterContainer.dataset.spriteLoadGen !== String(loadGen);

        return new Promise((resolve) => {
            const renderFallbackStill = () => {
                if (isStale()) {
                    resolve();
                    return;
                }
                const cssText = characterVideo.style.cssText;
                characterVideo.remove();
                characterContainer.querySelectorAll('img, video').forEach((el) => el.remove());
                if (isStale()) {
                    resolve();
                    return;
                }
                const fallbackImg = document.createElement('img');
                fallbackImg.src = fallbackSpriteUrl || DEFAULT_SPRITE;
                fallbackImg.alt = characterName;
                fallbackImg.style.cssText = cssText;
                fallbackImg.style.opacity = '1';
                characterContainer.appendChild(fallbackImg);
                try {
                    this.addCharacterInteractionEvents(fallbackImg, characterName, hangoutConfig, index);
                    initializeImageZoom();
                } catch (e) {
                    console.warn('Fallback image setup failed:', e);
                }
                resolve();
            };

            const timeout = setTimeout(() => {
                console.warn(`⏰ Video load timeout for ${characterName}, falling back to still sprite`);
                renderFallbackStill();
            }, 5000);

            const finish = () => {
                clearTimeout(timeout);
                if (isStale() || !characterVideo.isConnected) {
                    resolve();
                    return;
                }

                if (isFirstLoad) {
                    const isMobilePortrait = window.innerWidth <= 1024 && window.innerHeight > window.innerWidth;
                    const computedTransform = getComputedStyle(characterContainer).getPropertyValue('--character-transform');
                    if (!computedTransform || computedTransform === '') {
                        if (isMobilePortrait) {
                            characterContainer.style.setProperty('--character-transform', 'translateX(-50%)');
                        } else {
                            const totalChars = hangoutConfig?.allCharacters?.length || 1;
                            const positions = this._getPositionsForCount(totalChars);
                            const position = positions[index] || positions[0];
                            characterContainer.style.setProperty('--character-transform', position?.transform || 'translateX(-50%)');
                        }
                    }
                    if (isMobilePortrait) {
                        characterContainer.style.setProperty('transform', 'translateX(-50%)', 'important');
                        characterContainer.style.setProperty('left', '50%', 'important');
                        characterContainer.style.setProperty('right', 'auto', 'important');
                        characterContainer.style.removeProperty('opacity');
                        characterContainer.style.removeProperty('visibility');
                    } else {
                        void characterContainer.offsetHeight;
                        characterContainer.classList.add('character-slide-in', 'slide-in-staggered');
                        const handleAnimEnd = () => {
                            characterContainer.classList.remove('character-slide-in', 'slide-in-staggered');
                            if (this._isDesktopSingleContainer(characterContainer) && !characterContainer.classList.contains('user-positioned')) {
                                this._clearInlineCharacterPosition(characterContainer);
                            } else {
                                const finalTransform = characterContainer.style.getPropertyValue('--character-transform');
                                if (finalTransform) characterContainer.style.transform = finalTransform;
                            }
                            characterContainer.removeEventListener('animationend', handleAnimEnd);
                        };
                        characterContainer.addEventListener('animationend', handleAnimEnd);
                    }
                }

                (characterVideo).style.opacity = '1';
                requestAnimationFrame(() => {
                    if (isStale() || !characterVideo.isConnected) {
                        resolve();
                        return;
                    }
                    this.addCharacterInteractionEvents(characterVideo, characterName, hangoutConfig, index);
                    try {
                        initializeImageZoom();
                    } catch (error) {
                        console.warn('Image zoom initialization failed:', error);
                    }
                    resolve();
                });
            };

            characterVideo.onloadeddata = () => {
                if (isStale() || !characterVideo.isConnected) {
                    clearTimeout(timeout);
                    resolve();
                    return;
                }
                characterVideo.play().catch(() => { });
                finish();
            };
            characterVideo.onerror = () => {
                clearTimeout(timeout);
                console.warn(`Video load failed for ${characterName}, falling back to still sprite`);
                renderFallbackStill();
            };
            characterVideo.src = videoUrl;
        });
    }

    addCharacterInteractionEvents(characterImage, characterName, hangoutConfig, index) {
        characterImage.dataset.characterName = characterName;
        characterImage.tabIndex = 0;
        characterImage.role = 'button';
        characterImage.setAttribute('aria-label', `Mention ${characterName}`);

        const characterContainer = this._findCharacterContainer(characterImage, index);

        if (characterContainer) {
            this._setCharacterId(characterContainer, hangoutConfig, index, characterName);
            // Note: positioning is now handled during container creation, not here
        }

        // Call initializeImageZoom here to ensure it's set up for this specific image
        // Note: imageZoom now handles click events in its mouseup handler
        initializeImageZoom();

        // Remove old click/keydown handlers since imageZoom now handles bubble triggering
        characterImage.removeEventListener('click', this.handleCharacterClick);
        characterImage.removeEventListener('keydown', this.handleCharacterKeyDown);

        // Right-click / long-press context menu. Images offer "upgrade to video";
        // videos offer "upgrade video quality" (SpriteContextMenu branches by element type).
        if (characterContainer && (isImageElement(characterImage) || isVideoElement(characterImage))) {
            this._attachSpriteContextMenu(characterImage, characterContainer);
        }
    }

    /**
     * Attach context menu listeners (right-click and long-press) to sprite image
     * @param {HTMLImageElement} img - Sprite image element
     * @param {HTMLElement} container - Character container element
     */
    _attachSpriteContextMenu(img, container) {
        attachSpriteContextMenuListeners(img, {
            container,
            getOverrideSpriteData: () => ({ source: 'character-image' })
        });
    }

    _findCharacterContainer(characterImage, index) {
        return characterImage.closest('[class*="character-image"]') ||
            characterImage.closest('.character-image') ||
            document.querySelector(`.character-image-${index}`) ||
            Array.from(document.querySelectorAll('[class*="character-image"]'))
                .find(container => container.contains(characterImage));
    }

    _resolveCharacterIdForUpdate(hangoutConfig, characterName, index, spriteState = null) {
        const allCharacters = hangoutConfig?.allCharacters || [];
        const spriteCharacterId = spriteState && typeof spriteState === 'object'
            ? (spriteState.characterId ?? spriteState.character_id ?? null)
            : null;

        if (spriteCharacterId != null && String(spriteCharacterId) !== '') {
            return spriteCharacterId;
        }

        if (characterName) {
            const byName = allCharacters.find(
                (c) => String(c?.name || c?.characterName || '').toLowerCase() === String(characterName).toLowerCase()
            );
            if (byName?.id != null) return byName.id;
        }

        return allCharacters[index]?.id ?? null;
    }

    _setCharacterId(characterContainer, hangoutConfig, index, characterName = null) {
        const characterId = this._resolveCharacterIdForUpdate(hangoutConfig, characterName, index);
        if (characterId != null) {
            characterContainer.setAttribute('data-character-id', characterId);
        }
    }


    ensureCharacterVisibility(index = 0, isMultiCharacter = false) {
        const selector = isMultiCharacter ?
            `.character-image-${index}` :
            '.character-image:not([class*="character-image-"])';

        const characterContainer = document.querySelector(selector);
        if (!characterContainer) return;

        Object.assign((characterContainer).style, {
            visibility: 'visible',
            opacity: '1',
            display: 'flex'
        });

        // Reset off-screen positioning to the base transform
        const currentTransform = (characterContainer).style.transform || '';
        if (currentTransform.includes('translateX(-120vw)')) {
            // Restore to the base positioning transform from CSS variable
            const baseTransform = characterContainer.style.getPropertyValue('--character-transform') || 'translateX(0)';
            (characterContainer).style.transform = baseTransform;
        }
    }

    enableCharacterPositioning(characterContainer) {
        if (!characterContainer || characterContainer._positioningEnabled) return;

        characterContainer._positioningEnabled = true;

        Object.assign(characterContainer.style, {
            cursor: 'default'
        });
        // characterContainer.title = 'Click to view character bubble'; // Removed hover text
    }

    async handleCharacterClick(event, options = { playSound: true }) {
        // Prevent default and stop propagation to avoid conflicts with drag (only if real event)
        if (event.preventDefault) event.preventDefault();
        if (event.stopPropagation) event.stopPropagation();

        // Get characterId from the closest container instead of the image
        const container = event.target.closest('[class*="character-image"]') || event.target.closest('.character-image');
        const characterId = container ? container.getAttribute('data-character-id') : null;

        if (!characterId) {
            console.warn('Character click: no characterId found');
            return;
        }

        // Play character sound only if requested
        if (!options.playSound) {
            return;
        }

        try {
            // Get character's voice type name to determine sound folder
            let voiceType = 'smooth1'; // Default fallback

            try {
                const voiceIdPreferences = storage.get('characterVoiceIds') || {};
                const characterVoiceId = voiceIdPreferences[characterId];

                if (characterVoiceId && typeof characterVoiceId === 'string' && characterVoiceId.trim() !== '') {
                    const { getVoiceById } = await import('../../services/voiceService.js');
                    const voiceSubDirs = ['voices', 'male_voices'];
                    for (const subDir of voiceSubDirs) {
                        try {
                            const voiceData = await getVoiceById(characterVoiceId.trim(), subDir);
                            if (voiceData && voiceData.name) {
                                voiceType = voiceData.name.toLowerCase();
                                break;
                            }
                        } catch {
                            // Try next subDir
                        }
                    }
                }
            } catch (error) {
                console.warn('Error getting character voice type:', error);
            }

            // Pick a random sound (1, 2, or 3)
            const soundNumber = Math.floor(Math.random() * 3) + 1;
            const soundUrl = `${CDN_URL}/data/sounds/${voiceType}/sound${soundNumber}.mp3`;

            // Play the sound
            const audio = new Audio(soundUrl);
            audio.volume = 1.0; // Set volume to 100%

            audio.addEventListener('error', () => {
                console.warn(`Failed to load sound for ${voiceType}, trying default smooth1`);
                // Fallback to smooth1 if the voice-specific sound doesn't exist
                if (voiceType !== 'smooth1') {
                    const fallbackUrl = `${CDN_URL}/data/sounds/smooth1/sound${soundNumber}.mp3`;
                    const fallbackAudio = new Audio(fallbackUrl);
                    fallbackAudio.volume = 1.0;
                    fallbackAudio.play().catch(err => console.warn('Fallback sound failed:', err));
                }
            });

            audio.play().catch(err => console.warn('Error playing character sound:', err));

        } catch (err) {
            console.error('Error handling character click:', err);
        }
    }

    handleCharacterKeyDown(event) {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.handleCharacterClick(event, { playSound: false });
        }
    }

    async updateMentionHighlights() {
        const chatInput = document.getElementById('chat-input');
        if (!chatInput) return;

        const cursorPosition = chatInput.selectionStart;
        const currentValue = chatInput.value;

        let highlightDiv = document.getElementById('chat-input-highlight');
        if (!highlightDiv) {
            highlightDiv = this._createHighlightDiv(chatInput);
        }

        const characterNames = await this.getActiveCharacterNames();
        let styledContent = currentValue;

        const sortedNames = characterNames.sort((a, b) => b.length - a.length);
        for (const characterName of sortedNames) {
            const escapedName = characterName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const mentionRegex = new RegExp(`@(${escapedName})(?=\\s|[.,!?]|$)`, 'gi');
            styledContent = styledContent.replace(mentionRegex, '<span class="mention">@$1</span>');
        }

        highlightDiv.innerHTML = styledContent;
        chatInput.setSelectionRange(cursorPosition, cursorPosition);
    }

    _createHighlightDiv(chatInput) {
        const highlightDiv = document.createElement('div');
        highlightDiv.id = 'chat-input-highlight';
        highlightDiv.style.cssText = `
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            padding: 12px 20px;
            font-size: 16px;
            font-family: 'Poppins', sans-serif;
            color: transparent;
            pointer-events: none;
            white-space: pre-wrap;
            word-wrap: break-word;
            overflow: hidden;
            z-index: 1;
        `;

        chatInput.parentNode.style.position = 'relative';
        chatInput.parentNode.insertBefore(highlightDiv, chatInput);

        Object.assign(chatInput.style, {
            background: 'transparent',
            color: 'rgba(255, 255, 255, 0.9)',
            caretColor: 'var(--text-color)',
            zIndex: 2,
            position: 'relative'
        });

        return highlightDiv;
    }

    async getActiveCharacterNames() {
        const storageManager = await this._getStorageManager();
        const hangoutConfig = await storageManager.getItem('currentHangoutConfig');
        const activeCharacters = hangoutConfig?.allCharacters || [];

        if (activeCharacters.length === 0) {
            const hangoutState = HangoutState.getInstance();
            const selectedChar = hangoutState.getSelectedCharacter();
            return selectedChar?.name ? [selectedChar.name] : [];
        }

        return activeCharacters.map(char => char.name).filter(name => name?.trim());
    }

    async handleMentionDeletion(event) {
        if (event.key !== 'Backspace' && event.key !== 'Delete') return;

        const chatInput = event.target;
        const cursorPosition = chatInput.selectionStart;
        const currentValue = chatInput.value;
        const characterNames = await this.getActiveCharacterNames();

        if (event.key === 'Backspace' && cursorPosition > 0) {
            const beforeCursor = currentValue.slice(0, cursorPosition);

            for (const characterName of characterNames) {
                const fullMention = `@${characterName}`;
                if (beforeCursor.endsWith(fullMention)) {
                    event.preventDefault();
                    this._deleteMention(chatInput, cursorPosition - fullMention.length, cursorPosition);
                    return;
                }

                if (this._isInMention(beforeCursor, fullMention)) {
                    event.preventDefault();
                    const atIndex = beforeCursor.lastIndexOf('@');
                    this._deleteMention(chatInput, atIndex, cursorPosition);
                    return;
                }
            }
        }

        if (event.key === 'Delete' && cursorPosition < currentValue.length) {
            const afterCursor = currentValue.slice(cursorPosition);

            for (const characterName of characterNames) {
                const fullMention = `@${characterName}`;
                if (afterCursor.startsWith(fullMention)) {
                    event.preventDefault();
                    this._deleteMention(chatInput, cursorPosition, cursorPosition + fullMention.length);
                    return;
                }
            }
        }

        setTimeout(() => this.updateMentionHighlights(), 0);
    }

    _isInMention(beforeCursor, fullMention) {
        const atIndex = beforeCursor.lastIndexOf('@');
        if (atIndex === -1) return false;

        const potentialMention = beforeCursor.slice(atIndex);
        return fullMention.startsWith(potentialMention) && potentialMention.length > 1;
    }

    _deleteMention(chatInput, startPos, endPos) {
        const currentValue = chatInput.value;
        const newValue = currentValue.slice(0, startPos) + currentValue.slice(endPos);
        chatInput.value = newValue;
        chatInput.setSelectionRange(startPos, startPos);
        this.updateMentionHighlights();
    }

    async fetchCharacterSprite(
        characterId,
        actionId = null,
        outfitId = 0,
        index = 0,
        updateImage = true,
        isVideo = false,
        options = {}
    ) {
        const hangoutService = HangoutService.getInstance();
        const character = await hangoutService.getCharacterDetails(characterId);
        const characterGender = normalizeGender(character);
        const isFullSize = options.isFullSize !== false;

        try {
            const resolvedActionId = resolveLegacySpriteAction(actionId, characterGender)
                ?? getDefaultStandingActionId(characterGender);
            const useVideo = isFullSize
                ? await hasPlayableCharacterVideo(characterId, outfitId, resolvedActionId)
                : false;
            const data = await fetchCache.fetchCharacterSprite(
                characterId, resolvedActionId, outfitId, characterGender, isFullSize, useVideo
            );

            const characterName = character?.name || 'Character sprite';

            if (updateImage && data.spriteUrl) {
                const spriteState = actionId !== null ? String(actionId) : null;
                await this.updateCharacterImage(
                    data.spriteUrl,
                    characterName,
                    index,
                    spriteState,
                    false,
                    data.isVideo === true,
                    { skipLeftoverCleanup: options.skipLeftoverCleanup === true }
                );
                this._updateCharacterSession(characterId, characterName, outfitId, actionId);
            }

            return data.spriteUrl || DEFAULT_SPRITE;
        } catch (error) {
            console.error('Sprite fetch error:', error);
            return DEFAULT_SPRITE;
        }
    }

    _updateCharacterSession(characterId, characterName, outfitId, actionId) {
        const hangoutState = HangoutState.getInstance();
        const currentSession = hangoutState.getCurrentSession();

        if (!currentSession?.allCharacters) return;

        const characterIndex = currentSession.allCharacters.findIndex(c => c.id === characterId);
        if (characterIndex === -1) return;

        const character = currentSession.allCharacters[characterIndex];
        Object.assign(character, {
            name: characterName,
            outfitId: outfitId.toString(),
            spriteState: { action: actionId }
        });
    }

    async fetchCharacterSpriteUrl(characterId, actionId = null, outfitId = 0, options = {}) {
        return await this.fetchCharacterSprite(characterId, actionId, outfitId, 0, false, false, options);
    }

    async fetchCharacterSpriteUrlStrict(characterId, actionId = null, outfitId = 0, isVideo = false, options = {}) {
        return await this.fetchCharacterSprite(
            characterId,
            actionId,
            outfitId,
            0,
            false,
            isVideo,
            { allowEmotionFallback: false, ...options }
        );
    }

    async cleanupRemovedCharacters() {
        // Cinematic character removal is disabled because the animation system is stubbed.
        return;
    }

    _findCharacterBubbles(characterId, characterBubblesToRemove) {
        const bubbleContainer = document.getElementById('character-bubbles-container');
        if (!bubbleContainer || !characterId) return;

        const selectors = [
            `.character-message-bubble[data-character-id="${characterId}"]`,
            `.character-typing-indicator[data-typing-character-id="${characterId}"]`
        ];

        selectors.forEach(selector => {
            bubbleContainer.querySelectorAll(selector).forEach(element => {
                characterBubblesToRemove.push(element);
            });
        });
    }

    _repositionRemainingCharacters(activeCharacters) {
        if (activeCharacters.length === 0) return;

        for (let i = 0; i < activeCharacters.length; i++) {
            const container = document.querySelector(`.character-image-${i}`);
            if (container && (container).style.display !== 'none') {
                this.positionCharacterContainer(container, i, activeCharacters.length);
            }
        }
    }

    _repositionAllCharacters(hangoutConfig) {
        const activeCharacters = hangoutConfig?.allCharacters || [];
        const totalCharacters = activeCharacters.length;

        // Get expected positions for this character count
        const positions = this._getPositionsForCount(totalCharacters);

        for (let i = 0; i < totalCharacters; i++) {
            const container = document.querySelector(`.character-image-${i}`);
            if (container) {
                // If user has manually positioned this container, do NOT reset its placement/scale
                const isUserPositioned = container.classList && container.classList.contains('user-positioned');
                // Skip if currently animating
                const isAnimating = container.classList.contains('character-slide-in');

                if (!isUserPositioned && !isAnimating) {
                    const expectedPos = positions[i];
                    const currentLeft = (container).style.left;
                    const currentRight = (container).style.right;

                    // Only reposition if the expected position differs from current
                    const needsReposition =
                        (expectedPos?.left && currentLeft !== expectedPos.left) ||
                        (expectedPos?.right && currentRight !== expectedPos.right);

                    if (needsReposition) {
                        // Clear and reapply only if position actually changed
                        (container).style.left = '';
                        (container).style.right = '';
                        (container).style.transform = '';

                        this.positionCharacterContainer(container, i, totalCharacters);

                        // Reset all images to default scale for consistent sizing across all characters
                        const img = container.querySelector('img');
                        if (img) {
                            (img).style.transform = 'scale(1)';
                            (img).style.transformOrigin = 'bottom center';
                            // Clear any saved zoom state to prevent restoration
                            if ((img)._scale) (img)._scale = 1;
                        }
                    }
                } else {
                }
            } else {
                console.warn(`[CharacterManager] Container .character-image-${i} not found during repositioning`);
            }
        }
    }


    async _clearZoomStatesOnLayoutChange() {
        try {
            // Clear zoom states when layout changes to prevent positioning conflicts
            const { clearAllZoomStates } = await import('./imageZoom.js');
            if (clearAllZoomStates) {
                clearAllZoomStates();
            }
        } catch (error) {
            console.warn('Failed to clear zoom states on layout change, using fallback:', error);
            // Fallback: manually clear zoom states
            const images = document.querySelectorAll('[class*="character-image-"] > img');
            images.forEach(img => {
                if (img._resetTransform) {
                    img._resetTransform();
                }
            });
        }
    }

    async performCinematicCharacterRemoval(containersToRemove, characterBubblesToRemove) {
        // Store original states
        containersToRemove.forEach(({ container }) => {
            container._originalTransform = (container).style.transform || '';
        });

        characterBubblesToRemove.forEach(bubble => {
            bubble._originalTransform = bubble.style.transform || '';
            bubble._originalTransition = bubble.style.transition || '';
        });

        // Animate departures with staggered timing
        const slidePromises = containersToRemove.map(({ container, characterId }, index) => {
            return new Promise(resolve => {
                const delay = index * CHARACTER_STAGGER_DELAY;

                setTimeout(() => {
                    (container).style.transition = `transform ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.6, 1), opacity ${ANIMATION_DURATION}ms ease-in`;
                    (container).style.transform = 'translateX(-120vw) scale(0.6) rotate(-8deg)';
                    (container).style.opacity = '0';

                    // Animate associated bubbles
                    const characterBubbles = characterBubblesToRemove.filter(bubble =>
                        bubble.getAttribute('data-character-id') === characterId ||
                        bubble.getAttribute('data-typing-character-id') === characterId
                    );

                    characterBubbles.forEach(bubble => {
                        bubble.style.transition = `transform ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.6, 1), opacity ${ANIMATION_DURATION}ms ease-in`;
                        bubble.style.transform = 'translateX(-120vw) scale(0.8) rotate(-5deg)';
                        bubble.style.opacity = '0';
                    });
                }, delay);

                setTimeout(resolve, ANIMATION_DURATION + delay);
            });
        });

        await Promise.all(slidePromises);

        // Remove elements from DOM
        [...containersToRemove, ...characterBubblesToRemove.map(bubble => ({ container: bubble }))].forEach(({ container }) => {
            container.remove();
        });
    }

    async fallbackSimpleRemoval() {
        const storageManager = await this._getStorageManager();
        const hangoutConfig = await storageManager.getItem('currentHangoutConfig');
        const activeCharacters = hangoutConfig?.allCharacters || [];
        const activeCharacterIds = activeCharacters.map(char => String(char.id));

        const allCharacterContainers = document.querySelectorAll('[class*="character-image-"]');

        allCharacterContainers.forEach(container => {
            const containerIndex = parseInt(container.className.match(/character-image-(\d+)/)?.[1]);
            const characterId = container.getAttribute('data-character-id');

            const shouldRemove = (characterId && !activeCharacterIds.includes(characterId)) ||
                (!isNaN(containerIndex) && containerIndex >= activeCharacters.length);

            if (shouldRemove) {
                (container).style.transition = `opacity ${FADE_DURATION}ms ease, transform ${FADE_DURATION}ms ease`;
                (container).style.opacity = '0';
                (container).style.transform = 'scale(0.8)';

                setTimeout(() => container.remove(), FADE_DURATION);
            }
        });
    }

    async refreshAllCharacterSprites() {
        try {
            // Save current zoom states before refreshing sprites
            const { saveAllZoomStates } = await import('./imageZoom.js');
            saveAllZoomStates();
            const { getMessageDisplayManager } = await import('./message-display-manager.js');
            const displayManager = await getMessageDisplayManager();

            const storageManager = await this._getStorageManager();
            const hangoutConfig = await storageManager.getItem('currentHangoutConfig');
            const activeCharacters = hangoutConfig?.allCharacters || [];

            if (activeCharacters.length === 0) return;

            const hangoutService = HangoutService.getInstance();
            const allCharactersData = await hangoutService.getAvailableCharacters();
            const hangoutState = HangoutState.getInstance();
            const messages = hangoutState.getMessages();

            for (let index = 0; index < activeCharacters.length; index++) {
                const character = activeCharacters[index];
                await this._refreshSingleCharacterSprite(character, index, messages, allCharactersData);
            }
        } catch (error) {
            console.error('Sprite refresh error:', error);
            throw error;
        }
    }

    async _refreshSingleCharacterSprite(character, index, messages, allCharactersData) {
        const characterId = character.id;
        let actionId = null;
        let outfitId = character.outfitId || 0;

        if (Array.isArray(messages) && messages.length > 0) {
            for (let messageIndex = messages.length - 1; messageIndex >= 0; messageIndex -= 1) {
                const msg = messages[messageIndex];
                const isRosterNotice = msg?.isJoinMessage === true || msg?.isLeaveMessage === true;
                if ((!msg || msg.role !== 'assistant') && !isRosterNotice) continue;
                if (String(msg.characterId ?? msg.character_id) !== String(characterId)) continue;

                const extracted = resolveMessageSpriteData(msg, {
                    characterId,
                    fallbackOutfitId: outfitId
                });

                if (actionId === null && extracted.actionId !== null) {
                    actionId = extracted.actionId;
                }
                if ((outfitId === undefined || outfitId === null || outfitId === '0') && extracted.outfitId !== null) {
                    outfitId = extracted.outfitId;
                }

                if (actionId !== null && outfitId !== null) {
                    break;
                }
            }
        }

        if (actionId === null) {
            actionId = character.actionId;
        }
        if (outfitId === null || outfitId === undefined) outfitId = 0;

        await this.fetchCharacterSprite(
            characterId,
            actionId,
            outfitId,
            index,
            true
        );
    }

    async cleanupLeftoverSprites() {
        try {
            const storageManager = await this._getStorageManager();
            const hangoutConfig = await storageManager.getItem('currentHangoutConfig');
            const activeCharacters = hangoutConfig?.allCharacters || [];
            const activeCharacterIds = activeCharacters.map(char => String(char.id));

            const allCharacterContainers = document.querySelectorAll('[class*="character-image"]');
            const containersToRemove = [];
            const characterBubblesToRemove = [];
            const seenIndices = new Set();

            allCharacterContainers.forEach(container => {
                const containerIndex = parseInt(container.className.match(/character-image-(\d+)/)?.[1]);
                const characterId = container.getAttribute('data-character-id');

                const shouldRemove = (characterId && !activeCharacterIds.includes(characterId)) ||
                    (!isNaN(containerIndex) && containerIndex >= activeCharacters.length) ||
                    (!isNaN(containerIndex) && seenIndices.has(containerIndex));

                if (shouldRemove) {
                    containersToRemove.push(container);
                    this._findCharacterBubbles(characterId, characterBubblesToRemove);
                } else if (!isNaN(containerIndex)) {
                    seenIndices.add(containerIndex);
                }
            });

            // Remove with fade animation
            [...containersToRemove, ...characterBubblesToRemove].forEach(element => {
                element.style.transition = `opacity ${FADE_DURATION}ms ease, transform ${FADE_DURATION}ms ease`;
                element.style.opacity = '0';
                element.style.transform = 'scale(0.8)';

                setTimeout(() => element.remove(), FADE_DURATION);
            });
        } catch (error) {
            console.error('Sprite cleanup error:', error);
        }
    }

    hideCharacterForCinematicIntro(index = 0, isMultiCharacter = false) {
        const selector = isMultiCharacter ?
            `.character-image-${index}` :
            '.character-image:not([class*="character-image-"])';

        const characterContainer = document.querySelector(selector);
        if (!characterContainer) return;

        // Store original state for restoration
        Object.assign(characterContainer, {
            _originalTransform: characterContainer.style.transform || '',
            _originalVisibility: characterContainer.style.visibility || 'visible',
            _originalOpacity: characterContainer.style.opacity || 1
        });

        // Get the current character transform value
        const baseTransform = characterContainer.style.getPropertyValue('--character-transform') || 'translateX(0)';

        Object.assign(characterContainer.style, {
            // Compose transforms: base positioning + off-screen offset
            // This ensures consistent animation regardless of character position
            transform: `${baseTransform} translateX(-120vw) scale(0.8) rotate(5deg)`,
            opacity: 0,
            visibility: 'hidden'
        });
    }
}

// Create and export singleton instance
export const characterManager = new CharacterManager();

export const updateCharacterImage = (spriteUrl, characterName, index, spriteState, isInitialSetup, isVideo = false) =>
    characterManager.updateCharacterImage(spriteUrl, characterName, index, spriteState, isInitialSetup, isVideo);

export const fetchCharacterSprite = (characterId, actionId, outfitId, index, updateImage, isVideo = false, options = {}) =>
    characterManager.fetchCharacterSprite(characterId, actionId, outfitId, index, updateImage, isVideo, options);

export const fetchCharacterSpriteUrl = (characterId, actionId, outfitId, options = {}) =>
    characterManager.fetchCharacterSpriteUrl(characterId, actionId, outfitId, options);

export const fetchCharacterSpriteUrlStrict = (characterId, actionId, outfitId, isVideo = false, options = {}) =>
    characterManager.fetchCharacterSpriteUrlStrict(characterId, actionId, outfitId, isVideo, options);

export const cleanupRemovedCharacters = () => characterManager.cleanupRemovedCharacters();

export const refreshAllCharacterSprites = () => characterManager.refreshAllCharacterSprites();

/**
 * Lightweight style refresh for orientation changes
 * Updates inline styles on existing sprite images without reloading them
 */
export const refreshSpriteStylesForOrientation = () => {
    const isMobilePortrait = window.innerWidth <= 1024 && window.innerHeight > window.innerWidth;
    const maxConstraint = isMobilePortrait ? 'none' : '100%';
    const objectFit = isMobilePortrait ? 'contain' : 'cover';
    const objectPosition = objectFit === 'contain' ? 'bottom center' : 'center';

    // Update all character sprite images
    document.querySelectorAll('.character-image img, [class*="character-image-"] img').forEach(img => {
        img.style.maxWidth = maxConstraint;
        img.style.maxHeight = maxConstraint;
        img.style.objectFit = objectFit;
        img.style.objectPosition = objectPosition;
    });

};
